import { forwardRef, useId } from 'react';
import { cn } from './cn';

export interface SliderProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'value' | 'onChange' | 'min' | 'max' | 'step' | 'type' | 'size'
  > {
  /** Current value (controlled). */
  value: number;
  /** Called with the next numeric value on change. */
  onChange: (value: number) => void;
  /** Minimum value. Default 0. */
  min?: number;
  /** Maximum value. Default 100. */
  max?: number;
  /** Step increment. Default 1. */
  step?: number;
  disabled?: boolean;
  /** Field label rendered above the track. */
  label?: React.ReactNode;
  /** Show the current value next to the label. Default false. */
  showValue?: boolean;
  /** Custom renderer for the displayed value. */
  formatValue?: (value: number) => React.ReactNode;
  /** Class applied to the outer wrapper (label + track). */
  className?: string;
}

/**
 * Themed range slider — replaces MUI <Slider />.
 * Robust styled native <input type="range">: the filled portion is drawn with an
 * inline linear-gradient (bg-primary up to the value, bg-surface-2 after), and the
 * thumb is a white circle with a primary border + card shadow, styled per-instance
 * via a scoped <style> block for both WebKit and Firefox.
 */
export const Slider = forwardRef<HTMLInputElement, SliderProps>(
  (
    {
      value,
      onChange,
      min = 0,
      max = 100,
      step = 1,
      disabled,
      label,
      showValue = false,
      formatValue,
      className,
      id,
      'aria-label': ariaLabel,
      ...props
    },
    ref,
  ) => {
    const reactId = useId();
    const inputId = id ?? reactId;
    // Unique, CSS-safe class so the scoped thumb styles don't leak across instances.
    const scopeClass = `slider-${reactId.replace(/[^a-zA-Z0-9_-]/g, '')}`;

    const range = max - min;
    const ratio = range > 0 ? (value - min) / range : 0;
    const percent = Math.min(100, Math.max(0, ratio * 100));

    const filledColor = disabled ? 'rgb(var(--color-muted) / 0.5)' : 'rgb(var(--color-primary))';
    const trackColor = 'rgb(var(--color-surface-2))';
    const trackBackground = `linear-gradient(to right, ${filledColor} 0%, ${filledColor} ${percent}%, ${trackColor} ${percent}%, ${trackColor} 100%)`;

    const displayValue = formatValue ? formatValue(value) : value;
    const accessibleLabel =
      ariaLabel ?? (typeof label === 'string' ? label : undefined);

    return (
      <div className={cn('flex w-full flex-col gap-2', className)}>
        {(label || showValue) && (
          <div className="flex items-center justify-between gap-3">
            {label && (
              <label htmlFor={inputId} className="text-sm font-medium text-content">
                {label}
              </label>
            )}
            {showValue && (
              <span className="text-sm font-semibold tabular-nums text-primary">
                {displayValue}
              </span>
            )}
          </div>
        )}

        {/* Scoped thumb styles — themed via CSS custom properties, no hardcoded hex. */}
        <style>{`
          .${scopeClass} {
            -webkit-appearance: none;
            appearance: none;
          }
          .${scopeClass}::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            height: 18px;
            width: 18px;
            margin-top: -7px;
            border-radius: 9999px;
            background: rgb(var(--color-surface));
            border: 2px solid rgb(var(--color-primary));
            box-shadow: var(--shadow-card);
            cursor: pointer;
            transition: transform 0.15s ease, box-shadow 0.15s ease;
          }
          .${scopeClass}::-moz-range-thumb {
            height: 18px;
            width: 18px;
            border-radius: 9999px;
            background: rgb(var(--color-surface));
            border: 2px solid rgb(var(--color-primary));
            box-shadow: var(--shadow-card);
            cursor: pointer;
            transition: transform 0.15s ease, box-shadow 0.15s ease;
          }
          .${scopeClass}::-webkit-slider-runnable-track {
            height: 4px;
            border-radius: 9999px;
          }
          .${scopeClass}::-moz-range-track {
            height: 4px;
            border-radius: 9999px;
          }
          .${scopeClass}:not([disabled]):hover::-webkit-slider-thumb {
            transform: scale(1.1);
          }
          .${scopeClass}:not([disabled]):hover::-moz-range-thumb {
            transform: scale(1.1);
          }
          .${scopeClass}:not([disabled]):active::-webkit-slider-thumb {
            transform: scale(1.18);
          }
          .${scopeClass}:not([disabled]):active::-moz-range-thumb {
            transform: scale(1.18);
          }
          .${scopeClass}:focus-visible::-webkit-slider-thumb {
            box-shadow: var(--shadow-card), 0 0 0 4px rgb(var(--color-primary) / 0.4);
          }
          .${scopeClass}:focus-visible::-moz-range-thumb {
            box-shadow: var(--shadow-card), 0 0 0 4px rgb(var(--color-primary) / 0.4);
          }
          @media (prefers-reduced-motion: reduce) {
            .${scopeClass}::-webkit-slider-thumb,
            .${scopeClass}::-moz-range-thumb {
              transition: none;
            }
          }
        `}</style>

        <input
          ref={ref}
          id={inputId}
          type="range"
          value={value}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          aria-label={accessibleLabel}
          aria-valuetext={typeof displayValue === 'string' ? displayValue : undefined}
          onChange={(e) => onChange(Number(e.target.value))}
          style={{ background: trackBackground }}
          className={cn(
            scopeClass,
            'h-4 w-full cursor-pointer rounded-full bg-transparent outline-none',
            'focus-visible:outline-none',
            disabled && 'cursor-not-allowed opacity-60',
          )}
          {...props}
        />
      </div>
    );
  },
);

Slider.displayName = 'Slider';
